import { Box, Typography } from '@mui/material'
import type { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { getMarketColor } from '@/features/bidding/constants/marketColors'
import { useBidLadderForecast } from '@/features/bidding/hooks/useBidLadderForecast'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'
import {
  buildAggregateHourlyMap,
  resolveVolumeCapacityAverage,
} from '@/features/bidding/utils/cascading/bidLadder/resolveVolumeCapacity'
import { getMarketProgramNames } from '@/features/bidding/utils/forecast/marketProgramNames'
import { getMarketVolumeGranularity } from '@/features/bidding/utils/marketCapabilities'

type BidLadderChartsSymmetricalProps = {
  bidLevelInputs: BidLevelInputSymmetrical[]
  forecastsData: FormattedRow[] | undefined
  selectedDeliveryDay: DateTime
  totalMWAvailable: number
  market: ForecastMarketPair
  height?: number
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null
  aggregateAvailability?: AggregateAvailabilityResponse | null
}

/**
 * Simplified bid ladder chart for symmetrical markets (FCR-N).
 * Shows a single-sided bid curve instead of the butterfly layout.
 */
function BidLadderChartsSymmetrical({
  bidLevelInputs,
  forecastsData,
  selectedDeliveryDay,
  totalMWAvailable,
  market,
  height = 200,
  aggregateSuggestedBids,
  aggregateAvailability,
}: Readonly<BidLadderChartsSymmetricalProps>) {
  const { t } = useTranslation()
  const marketColor = getMarketColor(market)
  const marketNames = getMarketProgramNames(market)

  // Fetch forecast data for the market (same for both directions in symmetrical markets)
  const forecastData = useBidLadderForecast(forecastsData, marketNames.up, selectedDeliveryDay)

  // Convert bid levels to a simple step function for display
  const granularityMW = getMarketVolumeGranularity(market).stepMW
  const bidCurveData = useMemo(() => {
    const suggestedMap = buildAggregateHourlyMap(aggregateSuggestedBids, 'up')
    const availableMap = buildAggregateHourlyMap(aggregateAvailability, 'up')

    const sortedLevels = [...bidLevelInputs]
      .map((level, index) => {
        const groupMW = level.assignedGroups.reduce((sum, g) => sum + g.capacityMW, 0)
        const capacityMW =
          groupMW > 0
            ? groupMW
            : resolveVolumeCapacityAverage(level.volumeCapacity, suggestedMap, availableMap, granularityMW)
        return {
          levelIndex: index,
          price: level.price,
          capacityMW,
        }
      })
      .filter((level) => level.price !== null && level.capacityMW > 0)
      .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))

    // Build cumulative step function
    let cumulative = 0
    return sortedLevels.map((level) => {
      const start = cumulative
      cumulative += level.capacityMW
      return {
        ...level,
        startMW: start,
        endMW: cumulative,
      }
    })
  }, [bidLevelInputs, aggregateSuggestedBids, aggregateAvailability, granularityMW])

  // Calculate chart dimensions
  const chartHeight = height - 40 // Account for header
  const chartWidth = '100%'

  // Get max values for scaling
  const maxMW = Math.max(
    totalMWAvailable,
    bidCurveData.length > 0 ? (bidCurveData[bidCurveData.length - 1]?.endMW ?? 0) : 0,
  )
  const maxPrice = Math.max(forecastData?.clearingRangeHigh ?? 100, ...bidCurveData.map((d) => d.price ?? 0))

  // Helper to convert values to percentages
  const mwToPercent = (mw: number) => (maxMW > 0 ? (mw / maxMW) * 100 : 0)
  const priceToPercent = (price: number) => (maxPrice > 0 ? 100 - (price / maxPrice) * 100 : 100)

  // Chart should render if we have forecast data OR bid curve data
  const hasForecastData = forecastData !== null
  const hasBidCurveData = bidCurveData.length > 0
  const hasData = hasForecastData || hasBidCurveData

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.5,
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              backgroundColor: marketColor,
            }}
          />
          <span>{marketNames.displayName}</span>
          {forecastData && (
            <>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: marketColor,
                  opacity: 0.2,
                }}
              />
              <span>Clearing Range</span>
            </>
          )}
        </Box>
        <span>EUR/MWh vs MW</span>
      </Box>

      {/* Chart area */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          mx: 2,
          mb: 1,
          backgroundColor: 'grey.50',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {!hasData ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {t('bidding.bidding_cascading.charts.no_price_data')}
            </Typography>
          </Box>
        ) : (
          <svg height={chartHeight} style={{ width: chartWidth, display: 'block' }}>
            {/* Clearing range band (if forecast available) */}
            {forecastData && (
              <rect
                fill={marketColor}
                height={`${priceToPercent(forecastData.clearingRangeLow) - priceToPercent(forecastData.clearingRangeHigh)}%`}
                opacity={0.15}
                width="100%"
                x="0"
                y={`${priceToPercent(forecastData.clearingRangeHigh)}%`}
              />
            )}

            {/* Average clearing price line */}
            {forecastData && (
              <line
                stroke={marketColor}
                strokeDasharray="4 4"
                strokeWidth={1}
                x1="0"
                x2="100%"
                y1={`${priceToPercent(forecastData.avgClearingPrice)}%`}
                y2={`${priceToPercent(forecastData.avgClearingPrice)}%`}
              />
            )}

            {/* Bid step function */}
            {bidCurveData.map((step, i) => (
              <g key={step.levelIndex}>
                {/* Horizontal bar (price at this level) */}
                <rect
                  fill={marketColor}
                  height="3"
                  width={`${mwToPercent(step.endMW - step.startMW)}%`}
                  x={`${mwToPercent(step.startMW)}%`}
                  y={`calc(${priceToPercent(step.price ?? 0)}% - 1.5px)`}
                />
                {/* Vertical connector to next level */}
                {i < bidCurveData.length - 1 && (
                  <line
                    stroke={marketColor}
                    strokeWidth={3}
                    x1={`${mwToPercent(step.endMW)}%`}
                    x2={`${mwToPercent(step.endMW)}%`}
                    y1={`${priceToPercent(step.price ?? 0)}%`}
                    y2={`${priceToPercent(bidCurveData[i + 1].price ?? 0)}%`}
                  />
                )}
              </g>
            ))}

            {/* Total available MW indicator */}
            <line
              opacity={0.5}
              stroke="currentColor"
              strokeDasharray="2 2"
              strokeWidth={1}
              x1={`${mwToPercent(totalMWAvailable)}%`}
              x2={`${mwToPercent(totalMWAvailable)}%`}
              y1="0"
              y2="100%"
            />
          </svg>
        )}

        {/* Y-axis labels */}
        {hasData && (
          <>
            <Typography
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                fontSize: '0.625rem',
                color: 'text.secondary',
              }}
            >
              {maxPrice.toFixed(0)}
            </Typography>
            <Typography
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                fontSize: '0.625rem',
                color: 'text.secondary',
              }}
            >
              0
            </Typography>
          </>
        )}

        {/* X-axis labels */}
        {hasData && (
          <>
            <Typography
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                fontSize: '0.625rem',
                color: 'text.secondary',
              }}
            >
              {maxMW.toFixed(0)} MW
            </Typography>
          </>
        )}
      </Box>
    </Box>
  )
}

export default BidLadderChartsSymmetrical
